import type { TTableCellContext as CellContext, TTableRowSelection as RowSelectionState } from '@chocolate-soup-inc/cs-frontend-components';
import { ExtendedFAB, Icon, Modal, styleClassNames, tableClassNames, TableInner, Tooltip } from '@chocolate-soup-inc/cs-frontend-components';
import _ from 'lodash';
import { createRef, useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { serializeError } from 'serialize-error';
import { Filters, TFiltersProps } from '../../components/filters/Filters';
import { TablePage, TTablePageRef } from '../../components/tablePage/TablePage';
import { filterByRecipient, TShipmentWithAddressInfo, useShipmentAddressInfo, useShipmentsWithAddressInfo } from '../../entities/shipments/shared';
import { useCancelShipmentForPickupMutation, useSetShipmentAsPickedUpMutation } from '../../generated/graphql';

import { readableDate } from '@chocolate-soup-inc/cs-api-consumer-utils';
import clsx from 'clsx';
import { FloatingActions } from '../../components/floatingActions/FloatingActions';
import { useFilter } from '../../contexts/filters';
import { TShipmentType, useQueryAllOpenShipmentsPickupNEW } from '../../entities/shipments/queries';
import { TrackingDetailsModal } from './TrackingDetailsModal/TrackingDetailsModal';

export const RecipientCell = (context: CellContext<TShipmentType, unknown>) => {
  const { addressTypeIcon, recipientName, readableAddressType } = useShipmentAddressInfo(context.cell.row.original);

  return (
    <div className={clsx(styleClassNames.inlineLabel, tableClassNames.primary)}>
      {addressTypeIcon && readableAddressType && (
        <Tooltip autoPlacement={false} message={readableAddressType} placement='bottom'>
          <Icon filled={true} icon={addressTypeIcon} />
        </Tooltip>
      )}
      {recipientName}
    </div>
  );
};

export const ShipmentsSetForPickup = () => {
  const { filtersPageMode } = useFilter();
  useEffect(() => {
    filtersPageMode();
  }, []); //eslint-disable-line

  const tableRef = createRef<TTablePageRef>();

  const [companyFilter, setCompanyFilter] = useState<string>();
  const [recipientFilter, setRecipientFilter] = useState<string>();

  const { data: noAddressShipments, error, loading } = useQueryAllOpenShipmentsPickupNEW({ includingNextDays: 90 });

  const [selectedRows, setSelectedRows] = useState<string[]>();
  const [cancelPickupLoading, setCancelPickupLoading] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [recipientGifts, setRecipientGifts] = useState<any[] | null | undefined>(null);
  const [shipment, setShipment] = useState<TShipmentType | null | undefined>(null);

  const shipments = useShipmentsWithAddressInfo({
    shipments: noAddressShipments
  });

  const openModalDisplay = (original?: null | Record<string, any>) => {
    setShipment((original as TShipmentType) || {});
    setRecipientGifts(original?.gifts?.items || original?.gifts || []);
    setIsModalOpen(true);
  };

  const companies = useMemo(() => {
    return _.uniqBy(_.compact(shipments.map((s) => s.company)), 'id');
  }, [shipments]);

  const onRowSelectionChange = useCallback((rowSelection: RowSelectionState) => {
    console.log('selectedRows?:', rowSelection);
    setSelectedRows(Object.keys(rowSelection));
  }, []);

  const getRowId = useCallback((shipment: TShipmentType) => {
    return shipment.id;
  }, []);

  const [cancelShipmentForPickup] = useCancelShipmentForPickupMutation();

  const cancelPickup = useCallback(async () => {
    setCancelPickupLoading(true);
    let recordId: string | undefined;
    try {
      for (const id of selectedRows || []) {
        const shipment = shipments.find((item) => item.id === id);
        if (!shipment) continue;
        recordId = shipment.id;
        await cancelShipmentForPickup({ variables: { id: shipment.id, companyId: shipment.companyId, version: shipment._version } });
      }
      tableRef.current?.resetConfirmationModal();
      setSelectedRows(undefined);
    } catch (error) {
      console.error('Shipment cancelPickup failed', { shipmentId: recordId, error: serializeError(error) });
      toast.error('There was an error canceling the pickup.');
    } finally {
      setCancelPickupLoading(false);
    }
  }, [cancelShipmentForPickup, selectedRows, shipments, tableRef]);

  const cancelPickupClick = useCallback(() => {
    tableRef.current?.setConfirmationProps({
      headline: `Cancel Pickup for ${selectedRows?.length} Shipments`,
      supportingText: `Are you sure you want to cancel pickup for these ${selectedRows?.length} shipments?`,
      confirmLabel: 'Cancel Pickup',
      onConfirmClick: cancelPickup,
      open: true
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cancelPickup, selectedRows, tableRef, tableRef.current]);

  const [setAsPickedUpLoading, setSetAsPickedUpLoading] = useState<boolean>(false);

  const [setShipmentAsPickedUp] = useSetShipmentAsPickedUpMutation();

  const setAsPickedUp = useCallback(async () => {
    setSetAsPickedUpLoading(true);
    let recordId: string | undefined;
    try {
      for (const id of selectedRows || []) {
        const shipment = shipments.find((item) => item.id === id);
        if (!shipment || shipment.pickedUpAt != null) continue;
        recordId = shipment.id;
        await setShipmentAsPickedUp({ variables: { id: shipment.id, companyId: shipment.companyId, version: shipment._version } });
      }
      tableRef.current?.resetConfirmationModal();
      setSelectedRows(undefined);
    } catch (error) {
      console.error('Shipment setAsPickedUp failed', { shipmentId: recordId, error: serializeError(error) });
      toast.error('There was an error setting the shipments as picked up.');
    } finally {
      setSetAsPickedUpLoading(false);
    }
  }, [selectedRows, setShipmentAsPickedUp, shipments, tableRef]);

  const setAsPickedUpClick = useCallback(() => {
    tableRef.current?.setConfirmationProps({
      headline: `Set ${selectedRows?.length} Shipments as Picked Up?`,
      supportingText: `Are you sure you want to set these ${selectedRows?.length} shipments as picked up?`,
      confirmLabel: 'Set as Picked Up',
      onConfirmClick: setAsPickedUp,
      open: true
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRows, setAsPickedUp, tableRef, tableRef.current]);

  const filters = useMemo(() => {
    const filtersList: TFiltersProps['filters'] = [];

    filtersList.push({
      disabled: companies.length === 0,
      label: 'Company',
      name: 'companyId',
      onChange: (v) => {
        setCompanyFilter(v != null ? (v as string) : undefined);
      },
      options: companies?.map((c) => ({ label: c.name, value: c.id })),
      type: 'singleSelect',
      value: companyFilter
    });

    filtersList.push({
      label: 'Recipient',
      name: 'recipientName',
      onChange: (v) => {
        setRecipientFilter(v != null ? v : undefined);
      },
      type: 'textInput',
      value: recipientFilter
    });

    return filtersList;
  }, [companies, companyFilter, recipientFilter]);

  const filteredShipments = useMemo(() => {
    return shipments.filter((s) => {
      if (companyFilter != null && s.companyId !== companyFilter) return false;

      return filterByRecipient({
        shipment: s,
        filter: recipientFilter
      });
    });
  }, [companyFilter, recipientFilter, shipments]);

  //console.log('shipments OLD?:', shipments);
  console.log('filteredShipments OLD?:', filteredShipments);

  return (
    <TablePage actionsLoading={cancelPickupLoading || setAsPickedUpLoading} dataLoading={loading} error={error} ref={tableRef} title='Shipments for Pickup'>
      <Filters filters={filters} />
      <div style={{ overflow: 'auto' }}>
        <TableInner<TShipmentWithAddressInfo>
          enableSorting={true}
          defaultSorting={{ id: 'setForPickupAt', desc: false }}
          className={styleClassNames.scrollingTable}
          data={filteredShipments}
          emptyText={noAddressShipments.length === 0 ? 'No shipments set for pickup. When you set shipments for pickup, they will appear here.' : 'No shipments for the current filters.'}
          expandable={false}
          fillSelectedRows={true}
          fixedHeader={true}
          forceExpanded={true}
          getRowId={getRowId}
          hideSubRowsSelect={true}
          hoverableRows={!cancelPickupLoading}
          onRowSelectionChange={onRowSelectionChange}
          selectableRows={true}
          selectingChildSelectsFather={true}
          onRowClick={(e) => {
            openModalDisplay(e.original);
          }}
          columns={[
            {
              header: 'Company Name',
              accessorKey: 'company.name',
              cell: ({ cell }) => {
                const { pickedUpAt, companyId } = cell.row.original;
                const company = _.compact(companies || []).find((c) => c.id === companyId);

                return (
                  <div className={clsx(styleClassNames.inlineLabel, tableClassNames.primary)}>
                    {pickedUpAt && (
                      <Tooltip message={`Shipment was picked up on ${readableDate(pickedUpAt, 'do MMM, yyyy HH:mm')}`}>
                        <Icon icon='task_alt' />
                      </Tooltip>
                    )}
                    {company && <span>{company.name}</span>}
                  </div>
                );
              }
            },
            {
              header: 'Recipient',
              accessorKey: 'recipientName',
              cell: RecipientCell
            },
            {
              header: 'Shipping Date',
              accessorKey: 'shippingDate',
              cell: ({ cell }) => {
                return (
                  <div className={clsx(tableClassNames.tertiary)}>
                    <span>{readableDate(cell.row.original.actualShippingDate || cell.row.original.shippingDate || undefined)}</span>
                  </div>
                );
              }
            },
            {
              header: 'Set for Pickup At',
              accessorKey: 'setForPickupAt',
              cell: ({ cell }) => {
                return (
                  <div className={clsx(tableClassNames.primary)}>
                    <span>{readableDate(cell.row.original.setForPickupAt, 'do MMM, yyyy hh:mmaaa')}</span>
                  </div>
                );
              }
            },
            {
              header: 'Picked Up At',
              accessorKey: 'pickedUpAt',
              cell: ({ cell }) => {
                return (
                  <div className={clsx(tableClassNames.primary)}>
                    <span>{readableDate(cell.row.original.pickedUpAt, 'do MMM, yyyy hh:mmaaa')}</span>
                  </div>
                );
              }
            }
            /*{
            header: 'Tracking Number',
            cell: TrackingCell,
          },*/
          ]}
        />
      </div>
      {selectedRows && selectedRows.length > 0 && (
        <FloatingActions>
          <ExtendedFAB label={`Cancel Pickup for ${selectedRows.length} shipment${selectedRows.length > 1 ? 's' : ''}`} leadingIcon='exit_to_app' loading={cancelPickupLoading} onClick={cancelPickupClick} variant='tertiary' />
          <ExtendedFAB label={`Set ${selectedRows.length} shipment${selectedRows.length > 1 ? 's' : ''} as Picked Up`} leadingIcon='task_alt' loading={setAsPickedUpLoading} onClick={setAsPickedUpClick} variant='surface' />
        </FloatingActions>
      )}
      <Modal
        headline='Pickup Details'
        open={isModalOpen}
        size='large'
        closeModal={() => {
          setIsModalOpen(false);
        }}>
        <TrackingDetailsModal shipment={shipment} recipientGifts={recipientGifts} />
      </Modal>
    </TablePage>
  );
};
